import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";

// Cross-origin iframe DOM is not readable from this app. We gate visibility on
// WordPress "page exists for slug" (once per URL) plus iframe onLoad.

const WP_REST_API_BASE = "https://www.msuite.com/wp-json/wp/v2/pages";
const LOAD_TIMEOUT_MS = 8000;
const IFRAME_URL = "https://www.msuite.com/log-in/";

const getSlugFromUrl = (url) => {
  const match = url.match(/msuite\.com\/([^/]+)\/?$/);
  return match ? match[1] : null;
};

export default function MarketingContent() {
  /** null = first check pending, true = CMS reports page exists, false = unavailable */
  const [cmsOk, setCmsOk] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  const showIframe = cmsOk === true;
  const revealToUser = showIframe && iframeLoaded;

  useEffect(() => {
    setIframeLoaded(false);
  }, [showIframe]);

  useEffect(() => {
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = "https://www.msuite.com";
    preconnect.crossOrigin = "anonymous";
    document.head.appendChild(preconnect);

    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = "https://www.msuite.com";
    document.head.appendChild(dnsPrefetch);

    return () => {
      document.head.removeChild(preconnect);
      document.head.removeChild(dnsPrefetch);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const slug = getSlugFromUrl(IFRAME_URL);
    if (!slug) {
      setCmsOk(true);
      return () => {
        cancelled = true;
      };
    }

    setCmsOk(null);

    const runCheck = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
      try {
        const response = await fetch(
          `${WP_REST_API_BASE}?slug=${encodeURIComponent(slug)}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error("CMS check failed");
        const data = await response.json();
        if (!cancelled) {
          setCmsOk(Array.isArray(data) && data.length > 0);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (error.name === "AbortError") {
          setCmsOk(false);
          return;
        }
        console.warn("Marketing CMS availability check failed:", error.message);
        setCmsOk(false);
      } finally {
        inFlight = false;
      }
    };

    runCheck();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const handleIframeError = () => {
    setIframeLoaded(false);
  };

  const wrapperClassName = [
    "marketing-content-wrapper",
    revealToUser ? "marketing-content-wrapper--revealed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box flex={1} className={wrapperClassName}>
      {showIframe && (
        <iframe
          ref={iframeRef}
          src={IFRAME_URL}
          title="MSUITE Marketing Content"
          className="marketing-iframe"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          style={{
            opacity: revealToUser ? 1 : 0,
            position: revealToUser ? "static" : "absolute",
            pointerEvents: revealToUser ? "auto" : "none",
            top: 0,
            left: 0,
          }}
        />
      )}
    </Box>
  );
}
